/**
 * localStorage / sessionStorage の安全アクセス用ラッパー。
 *
 * Cookie/Storage を無効化したブラウザ（Safari private mode 等）で web storage に
 * アクセスすると `QuotaExceededError` 等の例外がスローされスクリプトが停止する。
 * SSR 環境では window が存在しない。両方とも捕捉して fallback 値を返す。
 */

type StorageKind = 'localStorage' | 'sessionStorage';

function operate<T>(kind: StorageKind, fn: (storage: Storage) => T, fallback: T): T {
  if (typeof window === 'undefined' || !(kind in window)) {
    return fallback;
  }
  try {
    return fn(window[kind]);
  } catch {
    return fallback;
  }
}

function createSafeStorage(kind: StorageKind) {
  return {
    getItem: (key: string): string | null => operate(kind, (s) => s.getItem(key), null),
    setItem: (key: string, value: string): void => operate(kind, (s) => s.setItem(key, value), undefined),
    removeItem: (key: string): void => operate(kind, (s) => s.removeItem(key), undefined),
    clear: (): void => operate(kind, (s) => s.clear(), undefined),
  };
}

export const safeSessionStorage = createSafeStorage('sessionStorage');
